import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { ComponentBase } from '../../classes/base/component-base';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConfigurationService } from '../../services/configuration.service';

export enum FilterMode {
  All = 'all',
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Quarter = 'quarter',
  Year = 'year',
  Custom = 'custom'
}

@Component({
  selector: 'date-filter-control',
  templateUrl: './date-filter-control.component.html',
  styleUrls: ['./date-filter-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateFilterControlComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None
})
export class DateFilterControlComponent extends ComponentBase implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {

  // ===== State =====
  isShowFilter: boolean = false;
  selectedMode: FilterMode = FilterMode.Month;
  selectedYear: number = new Date().getFullYear();
  selectedMonth: number = new Date().getMonth(); // 0-based
  selectedQuarter: number = Math.floor(new Date().getMonth() / 3);
  selectedDay: Date = new Date();
  selectedWeekStart: Date = null;
  selectedWeekEnd: Date = null;

  rangeDates: Date[] = [new Date(new Date().getFullYear(), new Date().getMonth(), 1), new Date()];
  vi: any;

  displayLabel: string = '';

  filterModes = [
    { label: 'Tất cả', value: FilterMode.All },
    { label: 'Theo ngày', value: FilterMode.Day },
    { label: 'Theo tuần', value: FilterMode.Week },
    { label: 'Theo tháng', value: FilterMode.Month },
    { label: 'Theo quý', value: FilterMode.Quarter },
    { label: 'Theo năm', value: FilterMode.Year },
    { label: 'Tùy chỉnh', value: FilterMode.Custom },
  ];

  months = [
    { label: 'T1', value: 0 }, { label: 'T2', value: 1 }, { label: 'T3', value: 2 },
    { label: 'T4', value: 3 }, { label: 'T5', value: 4 }, { label: 'T6', value: 5 },
    { label: 'T7', value: 6 }, { label: 'T8', value: 7 }, { label: 'T9', value: 8 },
    { label: 'T10', value: 9 }, { label: 'T11', value: 10 }, { label: 'T12', value: 11 },
  ];

  quarters = [
    { label: 'Quý 1', value: 0 },
    { label: 'Quý 2', value: 1 },
    { label: 'Quý 3', value: 2 },
    { label: 'Quý 4', value: 3 },
  ];

  calendarWeeks: { date: Date, day: number, isCurrentMonth: boolean, isToday: boolean }[][] = [];
  calendarMonthYear: Date = new Date();
  panelStyle: { [key: string]: string } = {};
  isPanelPositioned: boolean = false;

  @ViewChild('filterTrigger') filterTrigger: ElementRef;
  @ViewChild('filterPanel') filterPanel: ElementRef;

  @Output() rangeDatesChange = new EventEmitter<any>();
  @Input() refTab: number = -1;
  @Input() position: boolean = false;
  @Input() index: number = null;

  constructor(
    private _configurationService: ConfigurationService,
    private _eref: ElementRef
  ) {
    super();
  }

  ngOnInit() {
    this.vi = this._configurationService.calendarVietnamese;
    this.initFromLegacyIndex();
    this.buildCalendar();
  }

  ngAfterViewInit() {
    if (this.filterPanel && this.filterPanel.nativeElement && this.filterPanel.nativeElement.parentNode !== document.body) {
      document.body.appendChild(this.filterPanel.nativeElement);
    }
  }

  ngOnDestroy() {
    if (this.filterPanel && this.filterPanel.nativeElement && this.filterPanel.nativeElement.parentNode === document.body) {
      document.body.removeChild(this.filterPanel.nativeElement);
    }
  }

  // ===== Tương thích ngược với [index] cũ =====
  private initFromLegacyIndex() {
    const now = new Date();
    switch (this.index) {
      case 0: // Tất cả
        this.selectedMode = FilterMode.All;
        break;
      case 1: // Hôm nay
        this.selectedMode = FilterMode.Day;
        this.selectedDay = now;
        break;
      case 3: // Tuần này
        this.selectedMode = FilterMode.Week;
        break;
      case 5: // Tháng này
        this.selectedMode = FilterMode.Month;
        break;
      case 7: // Quý này
        this.selectedMode = FilterMode.Quarter;
        break;
      default:
        this.selectedMode = FilterMode.Month;
        break;
    }
    this.applyFilter();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const target = event.target as Node;
    const isInsideHost = this._eref.nativeElement.contains(target);
    const isInsidePanel = this.filterPanel && this.filterPanel.nativeElement.contains(target);

    if (!isInsideHost && !isInsidePanel) {
      this.isShowFilter = false;
      this.isPanelPositioned = false;
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.queuePanelPositionUpdate();
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    this.queuePanelPositionUpdate();
  }

  toggleFilter(event: MouseEvent) {
    event.stopPropagation();

    if (this.isShowFilter) {
      this.closeFilter();
      return;
    }

    this.isShowFilter = true;
    this.isPanelPositioned = false;
    this.queuePanelPositionUpdate(true);
  }

  closeFilter() {
    this.isShowFilter = false;
    this.isPanelPositioned = false;
  }

  private queuePanelPositionUpdate(markAsPositioned: boolean = false) {
    if (!this.isShowFilter) {
      return;
    }

    window.requestAnimationFrame(() => {
      this.updatePanelPosition();

      if (markAsPositioned && this.isShowFilter) {
        this.isPanelPositioned = true;
      }
    });
  }

  private updatePanelPosition() {
    if (!this.isShowFilter || !this.filterTrigger || !this.filterPanel) {
      return;
    }

    const triggerElement = this.filterTrigger.nativeElement as HTMLElement;
    const panelElement = this.filterPanel.nativeElement as HTMLElement;
    const triggerRect = triggerElement.getBoundingClientRect();
    const panelRect = panelElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const viewportPadding = 8;
    const gap = 6;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft || 0;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;

    let left = this.position ? triggerRect.right + scrollX - panelRect.width : triggerRect.left + scrollX;
    left = Math.max(scrollX + viewportPadding, Math.min(left, scrollX + viewportWidth - panelRect.width - viewportPadding));

    let top = triggerRect.bottom + scrollY + gap;
    if (top + panelRect.height > scrollY + viewportHeight - viewportPadding) {
      top = Math.max(scrollY + viewportPadding, triggerRect.top + scrollY - panelRect.height - gap);
    }

    this.panelStyle = {
      left: `${Math.round(left)}px`,
      top: `${Math.round(top)}px`
    };
  }

  // ===== Navigate prev/next trên thanh input =====
  navigatePrev(event: MouseEvent) {
    event.stopPropagation();
    switch (this.selectedMode) {
      case FilterMode.Day:
        this.selectedDay = new Date(this.selectedDay.getFullYear(), this.selectedDay.getMonth(), this.selectedDay.getDate() - 1);
        this.calendarMonthYear = new Date(this.selectedDay);
        this.buildCalendar();
        break;
      case FilterMode.Week:
        if (!this.selectedWeekStart) this.computeCurrentWeek();
        this.selectedWeekStart.setDate(this.selectedWeekStart.getDate() - 7);
        this.selectedWeekEnd.setDate(this.selectedWeekEnd.getDate() - 7);
        this.selectedWeekStart = new Date(this.selectedWeekStart);
        this.selectedWeekEnd = new Date(this.selectedWeekEnd);
        break;
      case FilterMode.Month:
        this.selectedMonth--;
        if (this.selectedMonth < 0) { this.selectedMonth = 11; this.selectedYear--; }
        break;
      case FilterMode.Quarter:
        this.selectedQuarter--;
        if (this.selectedQuarter < 0) { this.selectedQuarter = 3; this.selectedYear--; }
        break;
      case FilterMode.Year:
        this.selectedYear--;
        break;
      default:
        return;
    }
    this.applyFilter();
  }

  navigateNext(event: MouseEvent) {
    event.stopPropagation();
    switch (this.selectedMode) {
      case FilterMode.Day:
        this.selectedDay = new Date(this.selectedDay.getFullYear(), this.selectedDay.getMonth(), this.selectedDay.getDate() + 1);
        this.calendarMonthYear = new Date(this.selectedDay);
        this.buildCalendar();
        break;
      case FilterMode.Week:
        if (!this.selectedWeekStart) this.computeCurrentWeek();
        this.selectedWeekStart.setDate(this.selectedWeekStart.getDate() + 7);
        this.selectedWeekEnd.setDate(this.selectedWeekEnd.getDate() + 7);
        this.selectedWeekStart = new Date(this.selectedWeekStart);
        this.selectedWeekEnd = new Date(this.selectedWeekEnd);
        break;
      case FilterMode.Month:
        this.selectedMonth++;
        if (this.selectedMonth > 11) { this.selectedMonth = 0; this.selectedYear++; }
        break;
      case FilterMode.Quarter:
        this.selectedQuarter++;
        if (this.selectedQuarter > 3) { this.selectedQuarter = 0; this.selectedYear++; }
        break;
      case FilterMode.Year:
        this.selectedYear++;
        break;
      default:
        return;
    }
    this.applyFilter();
  }

  canNavigate(): boolean {
    return this.selectedMode !== FilterMode.All && this.selectedMode !== FilterMode.Custom;
  }

  // ===== ControlValueAccessor =====
  onChangeControl = (obj: any) => { };
  onTouched = () => { };

  writeValue(obj: any): void {
    if (obj && Array.isArray(obj) && obj.length === 2) {
      this.rangeDates = obj;
    }
  }
  registerOnChange(fn: any): void {
    this.onChangeControl = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void { }

  // ===== Mode selection =====
  selectMode(mode: FilterMode) {
    this.selectedMode = mode;

    if (mode === FilterMode.All) {
      this.applyFilter();
      this.closeFilter();
    } else if (mode === FilterMode.Day) {
      this.calendarMonthYear = new Date(this.selectedDay);
      this.buildCalendar();
      this.applyFilter();
    } else if (mode === FilterMode.Week) {
      this.calendarMonthYear = new Date();
      this.computeCurrentWeek();
      this.buildCalendar();
      this.applyFilter();
    } else if (mode === FilterMode.Month) {
      this.applyFilter();
    } else if (mode === FilterMode.Quarter) {
      this.applyFilter();
    } else if (mode === FilterMode.Year) {
      this.applyFilter();
    } else if (mode === FilterMode.Custom) {
      this.updateDisplayLabel();
    }
  }

  // ===== Year navigation =====
  prevYear() { this.selectedYear--; }
  nextYear() { this.selectedYear++; }

  // ===== Month selection =====
  selectMonth(monthIndex: number) {
    this.selectedMonth = monthIndex;
    this.applyFilter();
    this.closeFilter();
  }

  // ===== Quarter selection =====
  selectQuarter(quarterIndex: number) {
    this.selectedQuarter = quarterIndex;
    this.applyFilter();
    this.closeFilter();
  }

  // ===== Year selection =====
  selectYear() {
    this.applyFilter();
    this.closeFilter();
  }

  // ===== Day calendar =====
  prevCalendarMonth() {
    this.calendarMonthYear = new Date(this.calendarMonthYear.getFullYear(), this.calendarMonthYear.getMonth() - 1, 1);
    this.buildCalendar();
  }

  nextCalendarMonth() {
    this.calendarMonthYear = new Date(this.calendarMonthYear.getFullYear(), this.calendarMonthYear.getMonth() + 1, 1);
    this.buildCalendar();
  }

  buildCalendar() {
    const year = this.calendarMonthYear.getFullYear();
    const month = this.calendarMonthYear.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    let startDow = firstDay.getDay(); // 0=CN
    if (startDow === 0) startDow = 7; // CN = 7

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days: { date: Date, day: number, isCurrentMonth: boolean, isToday: boolean }[] = [];

    // Ngày tháng trước
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDow - 1; i >= 1; i--) {
      const d = new Date(year, month - 1, prevMonthLastDay - i + 1);
      days.push({ date: d, day: d.getDate(), isCurrentMonth: false, isToday: d.getTime() === today.getTime() });
    }

    // Ngày tháng hiện tại
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      days.push({ date: date, day: d, isCurrentMonth: true, isToday: date.getTime() === today.getTime() });
    }

    // Ngày tháng sau
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      const date = new Date(year, month + 1, d);
      days.push({ date: date, day: d, isCurrentMonth: false, isToday: date.getTime() === today.getTime() });
    }

    // Chia thành tuần
    this.calendarWeeks = [];
    for (let i = 0; i < days.length; i += 7) {
      this.calendarWeeks.push(days.slice(i, i + 7));
    }
  }

  selectDay(day: { date: Date }) {
    this.selectedDay = day.date;
    this.applyFilter();
    this.closeFilter();
  }

  isDaySelected(day: { date: Date }): boolean {
    if (!this.selectedDay) return false;
    return day.date.toDateString() === this.selectedDay.toDateString();
  }

  // ===== Week =====
  computeCurrentWeek() {
    const now = new Date();
    const day = now.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    this.selectedWeekStart = new Date(now);
    this.selectedWeekStart.setDate(now.getDate() + diffToMonday);
    this.selectedWeekStart.setHours(0, 0, 0, 0);
    this.selectedWeekEnd = new Date(this.selectedWeekStart);
    this.selectedWeekEnd.setDate(this.selectedWeekStart.getDate() + 6);
    this.selectedWeekEnd.setHours(23, 59, 59, 999);
  }

  selectWeek(week: { date: Date }[]) {
    this.selectedWeekStart = new Date(week[0].date);
    this.selectedWeekStart.setHours(0, 0, 0, 0);
    this.selectedWeekEnd = new Date(week[6].date);
    this.selectedWeekEnd.setHours(23, 59, 59, 999);
    this.applyFilter();
    this.closeFilter();
  }

  isWeekSelected(week: { date: Date }[]): boolean {
    if (!this.selectedWeekStart) return false;
    return week[0].date.toDateString() === this.selectedWeekStart.toDateString();
  }

  isDayInSelectedWeek(day: { date: Date }): boolean {
    if (!this.selectedWeekStart || !this.selectedWeekEnd) return false;
    const d = day.date.getTime();
    return d >= this.selectedWeekStart.getTime() && d <= this.selectedWeekEnd.getTime();
  }

  // ===== Custom range =====
  onDateRangeSelect() {
    if (this.rangeDates[0] && this.rangeDates[1]) {
      this.emitRange(this.rangeDates[0], this.rangeDates[1]);
      this.updateDisplayLabel();
      this.closeFilter();
    }
  }

  // ===== Apply filter =====
  applyFilter(emit: boolean = true) {
    let from: Date;
    let to: Date;

    switch (this.selectedMode) {
      case FilterMode.All:
        from = new Date('1900-01-01');
        to = new Date();
        break;

      case FilterMode.Day:
        from = new Date(this.selectedDay);
        from.setHours(0, 0, 0, 0);
        to = new Date(this.selectedDay);
        to.setHours(23, 59, 59, 999);
        break;

      case FilterMode.Week:
        if (!this.selectedWeekStart) this.computeCurrentWeek();
        from = new Date(this.selectedWeekStart);
        to = new Date(this.selectedWeekEnd);
        break;

      case FilterMode.Month:
        from = new Date(this.selectedYear, this.selectedMonth, 1);
        to = new Date(this.selectedYear, this.selectedMonth + 1, 0, 23, 59, 59, 999);
        break;

      case FilterMode.Quarter:
        const qStart = this.selectedQuarter * 3;
        from = new Date(this.selectedYear, qStart, 1);
        to = new Date(this.selectedYear, qStart + 3, 0, 23, 59, 59, 999);
        break;

      case FilterMode.Year:
        from = new Date(this.selectedYear, 0, 1);
        to = new Date(this.selectedYear, 11, 31, 23, 59, 59, 999);
        break;

      case FilterMode.Custom:
        return; // custom xử lý riêng qua calendar

      default:
        return;
    }

    if (emit) {
      this.emitRange(from, to);
    }
    this.updateDisplayLabel();
  }

  private emitRange(from: Date, to: Date) {
    this.rangeDates = [from, to];
    this.rangeDatesChange.emit(this.rangeDates);
    this.onChangeControl(this.rangeDates);
  }

  updateDisplayLabel() {
    switch (this.selectedMode) {
      case FilterMode.All:
        this.displayLabel = 'Tất cả';
        break;
      case FilterMode.Day:
        this.displayLabel = this.formatDate(this.selectedDay);
        break;
      case FilterMode.Week:
        this.displayLabel = this.selectedWeekStart && this.selectedWeekEnd
          ? `${this.formatDate(this.selectedWeekStart)} - ${this.formatDate(this.selectedWeekEnd)}`
          : 'Tuần này';
        break;
      case FilterMode.Month:
        this.displayLabel = `Tháng ${this.selectedMonth + 1}/${this.selectedYear}`;
        break;
      case FilterMode.Quarter:
        this.displayLabel = `Quý ${this.selectedQuarter + 1}/${this.selectedYear}`;
        break;
      case FilterMode.Year:
        this.displayLabel = `Năm ${this.selectedYear}`;
        break;
      case FilterMode.Custom:
        if (this.rangeDates[0] && this.rangeDates[1]) {
          this.displayLabel = `${this.formatDate(this.rangeDates[0])} - ${this.formatDate(this.rangeDates[1])}`;
        } else {
          this.displayLabel = 'Tùy chỉnh';
        }
        break;
    }
  }

  private formatDate(d: Date): string {
    if (!d) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }

  getCalendarTitle(): string {
    const monthNames = ['Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6',
      'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'];
    return `${monthNames[this.calendarMonthYear.getMonth()]} ${this.calendarMonthYear.getFullYear()}`;
  }
}
